"use client";
import React, { useState } from "react";
import {
  motion,
  AnimatePresence,
  useScroll,
  useMotionValueEvent,
} from "framer-motion";
import Link from "next/link";
import { Menu, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { LanguageSwitcher } from "./language-switcher";
import { useTranslations } from "next-intl";

// Navigation items with translation keys
const navItemsConfig = [
  { key: "home", link: "/" },
  { key: "techLab", link: "/tech-lab" },
  { key: "manifiesto", link: "/manifiesto" },
  { key: "contact", link: "/contact" },
];

export const FloatingNav = ({
  className,
}: {
  className?: string;
}) => {
  const t = useTranslations("navigation");
  const { scrollYProgress } = useScroll();

  const [visible, setVisible] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);

  useMotionValueEvent(scrollYProgress, "change", (current) => {
    if (typeof current === "number") {
      let direction = current! - scrollYProgress.getPrevious()!;

      if (scrollYProgress.get() < 0.05) {
        setVisible(true);
      } else {
        if (direction < 0) {
          setVisible(true);
        } else {
          setVisible(false);
          // Auto-close mobile menu when navbar hides on scroll-down
          setMobileOpen(false);
        }
      }
    }
  });

  return (
    <AnimatePresence mode="wait">
      <motion.div
        initial={{ opacity: 1, y: -100 }}
        animate={{
          y: visible ? 0 : -100,
          opacity: visible ? 1 : 0,
        }}
        transition={{ duration: 0.2 }}
        className={cn(
          // Mobile: compact pill (logo + hamburger). Desktop: full inline nav.
          "fixed z-[5000] top-6 md:top-10 inset-x-4 md:inset-x-0 mx-auto",
          "max-w-fit md:max-w-fit lg:min-w-fit",
          "rounded-lg border border-black/.1",
          "shadow-[0px_2px_3px_-1px_rgba(0,0,0,0.1),0px_1px_0px_0px_rgba(25,28,33,0.02),0px_0px_0px_1px_rgba(25,28,33,0.08)]",
          className
        )}
        style={{
          backdropFilter: "blur(16px) saturate(180%)",
          backgroundColor: "rgba(17, 25, 40, 0.75)",
          borderRadius: "12px",
          border: "1px solid rgba(255, 255, 255, 0.125)",
        }}
      >
        {/* Desktop: inline links + locale (md and up) */}
        <div className="hidden md:flex items-center justify-center space-x-4 px-10 py-5">
          {navItemsConfig.map((navItem, idx) => (
            <Link
              key={`link=${idx}`}
              href={navItem.link}
              className={cn(
                "relative dark:text-neutral-50 items-center flex space-x-1 text-neutral-600 dark:hover:text-neutral-300 hover:text-neutral-500"
              )}
            >
              <span className="text-sm !cursor-pointer">{t(navItem.key)}</span>
            </Link>
          ))}
          <div className="ml-2 pl-2 border-l border-white/10">
            <LanguageSwitcher />
          </div>
        </div>

        {/* Mobile: compact bar with hamburger (below md) */}
        <div className="flex md:hidden items-center justify-between gap-3 px-5 py-3 min-w-[260px]">
          <Link
            href="/"
            className="text-neutral-50 text-sm font-semibold"
            onClick={() => setMobileOpen(false)}
          >
            Holistika
          </Link>
          <button
            type="button"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileOpen}
            className="flex items-center justify-center w-9 h-9 rounded-md text-neutral-50 hover:bg-white/10 transition-colors"
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>

        {/* Mobile drawer (slide down below the pill) */}
        <AnimatePresence>
          {mobileOpen && (
            <motion.nav
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="md:hidden overflow-hidden border-t border-white/10"
            >
              <ul className="flex flex-col items-stretch py-2">
                {navItemsConfig.map((navItem, idx) => (
                  <li key={`mobile-link-${idx}`}>
                    <Link
                      href={navItem.link}
                      onClick={() => setMobileOpen(false)}
                      className="block px-5 py-3 text-sm text-neutral-50 hover:bg-white/10 transition-colors"
                    >
                      {t(navItem.key)}
                    </Link>
                  </li>
                ))}
                <li className="px-5 py-3 border-t border-white/10 mt-1">
                  <LanguageSwitcher />
                </li>
              </ul>
            </motion.nav>
          )}
        </AnimatePresence>
      </motion.div>
    </AnimatePresence>
  );
};
